import React, { useEffect, useMemo, useState } from "react";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { required } from "../../../../utils/formValidation";

const createSchema = {
  name: [required("Role name is required.")],
};

const RoleFormDialog = ({
  open,
  mode = "create",
  role = null,
  allowSuggestions = [],
  loading = false,
  tenantMode = false,
  onClose,
  onSubmit,
}) => {
  const isCreate = mode === "create";
  const [formError, setFormError] = useState("");
  const [allow, setAllow] = useState([]);

  const { values, getFieldProps, validateAll, reset } = useFormValidation(
    { name: "" },
    createSchema
  );

  useEffect(() => {
    if (!open) return;
    setFormError("");
    reset({ name: role?.name || "" });
    setAllow(Array.isArray(role?.allow) ? [...role.allow] : []);
  }, [open, reset, role]);

  const options = useMemo(
    () => Array.from(new Set([...(allowSuggestions || []), ...allow].filter(Boolean))),
    [allow, allowSuggestions]
  );

  const handleSubmit = async () => {
    if (isCreate) {
      const { isValid, errors } = validateAll();
      if (!isValid) {
        setFormError(errors.name || "Please correct the highlighted fields.");
        return;
      }
    }
    if ((!tenantMode || !isCreate) && !allow.length) {
      setFormError("Add at least one permission.");
      return;
    }

    setFormError("");
    try {
      if (isCreate) {
        await onSubmit(
          tenantMode
            ? { name: values.name.trim() }
            : { name: values.name.trim(), allow }
        );
      } else {
        await onSubmit(tenantMode ? { permissions: allow } : { allow });
      }
    } catch (err) {
      setFormError(err?.message || "Unable to save role.");
    }
  };

  return (
    <FormDialog
      open={open}
      title={isCreate ? "Add Role" : "Edit Role"}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel={isCreate ? "Create Role" : "Save Changes"}
      loadingLabel="Saving..."
    >
      <FormField
        label="Role Name"
        required={isCreate}
        disabled={!isCreate || loading}
        {...getFieldProps("name")}
      />
      {!tenantMode || !isCreate ? (
        <ChipAutocomplete
          label="Permissions"
          required
          freeSolo
          options={options}
          value={allow}
          onChange={setAllow}
          disabled={loading}
          helperText={tenantMode ? "Example: permission1" : "Example: idp.*.user.manage"}
        />
      ) : null}
    </FormDialog>
  );
};

export default RoleFormDialog;
